import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import styles from './AddSceneModal.module.css';
import { CreateBrandModal } from './CreateBrandModal';
import LogoManagementModal from '../../LogoManagementModal';
import { MaterialsPickerModal } from '../../materials/MaterialsPickerModal';
import { Tooltip } from '../../common/Tooltip';
import { listBrands } from '../../../features/brands/api';
import type { ProductBrandInfo } from '../../../features/scenarios/api';

interface AddSceneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectManual: () => void;
  onSelectAiContinue: () => void;
  onSelectProductCut: () => void;
  onSelectLogoCut: () => void;
  onSelectTitleCard?: () => void;
  onAttachFromMaterial?: (position: 'start' | 'end', imageUrl: string, materialId?: string) => void;
  hasTitleCard?: boolean;
  hasEndcard?: boolean;
  hasLogo: boolean;
  hasBrand: boolean;
  brandId?: string | null;
  isGenerating: boolean;
  generatingLabel?: string;
  scenarioId?: string;
  // Product-brand linking
  brandName?: string;
  productBrandInfo?: ProductBrandInfo | null;
  onLinkProduct?: () => void;
  onSkipLink?: () => void;
  linkProductStatus?: 'idle' | 'pending' | 'success';
  onBrandJustCreated?: (brandId: string) => void;
  onBrandLinked?: (brandId: string, linkProduct: boolean) => void;
  autoGenerateFrame?: boolean;
  onAutoGenerateFrameChange?: (value: boolean) => void;
  // 素材ピッカーのアスペクト比デフォルトフィルタ (シナリオのアスペクト比)
  aspectRatio?: string;
  // ストーリーボード(絵コンテ)モードか。AI生成の静止画カット(商品/タイトル/エンド)は
  // フレーム自動生成がスキップされ未完成カードになるため、storyboard では非表示にする。
  // ストーリーボードでは「既存素材を挿入」(動画) のみを使う。
  isStoryboard?: boolean;
}

export function AddSceneModal({
  isOpen,
  onClose,
  onSelectManual,
  onSelectAiContinue,
  onSelectProductCut,
  onSelectLogoCut,
  onSelectTitleCard,
  onAttachFromMaterial,
  hasTitleCard,
  hasEndcard,
  hasLogo,
  hasBrand,
  brandId,
  isGenerating,
  generatingLabel,
  scenarioId,
  brandName,
  productBrandInfo,
  onLinkProduct,
  onSkipLink,
  linkProductStatus,
  onBrandJustCreated,
  onBrandLinked,
  autoGenerateFrame,
  onAutoGenerateFrameChange,
  aspectRatio,
  isStoryboard = false,
}: AddSceneModalProps) {
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const [justCreatedBrand, setJustCreatedBrand] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showBrandSelector, setShowBrandSelector] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  // 素材から継ぎ足すブランドカット位置 (null=ピッカー非表示)
  const [materialPickerPosition, setMaterialPickerPosition] = useState<'start' | 'end' | null>(null);

  // ブランド一覧取得（ブランドなし＋セレクタ表示時のみ）
  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      return await listBrands();
    },
    staleTime: 5 * 60 * 1000,
    enabled: !hasBrand && showBrandSelector,
  });
  const brands: { id: string; brand_name: string }[] = brandsData?.brands ?? [];
  const selectedBrandName = brands.find(b => b.id === selectedBrandId)?.brand_name || '';

  const showLinkPrompt = isGenerating
    && justCreatedBrand
    && productBrandInfo?.product_type != null
    && !productBrandInfo?.current_brand_id
    && linkProductStatus !== 'success';

  if (!isOpen) return null;

  // transform/backdrop-filter を持つ祖先 (Studio レイアウト) が position:fixed を捕捉して
  // モーダルがビューポート基準で中央に出ない問題を避けるため、body 直下に portal する。
  const modalContent = (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        <div className={styles.header}>
          <h3 className={styles.title}>シーンを追加</h3>
          <button className={styles.closeButton} onClick={onClose} type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className={styles.content}>
          {/* 1列目: 新しいシーンを生成 */}
          <div className={styles.column}>
            <div className={styles.columnTitle}>新しいシーンを生成</div>
          {/* 自分でシナリオ入力 */}
          <button
            type="button"
            className={styles.optionCard}
            onClick={onSelectManual}
            disabled={isGenerating}
          >
            <div className={styles.optionIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            </div>
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>自分でシナリオ入力</div>
              <div className={styles.optionDescription}>空白のシーンを追加して手動で入力します</div>
            </div>
          </button>

          {/* AIにシーンの続きを提案させる */}
          <button
            type="button"
            className={styles.optionCard}
            onClick={onSelectAiContinue}
            disabled={isGenerating}
          >
            <div className={styles.optionIcon}>
              <img src="/reward_stars_2.svg" alt="" width="20" height="20" />
            </div>
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>AIにシーンの続きを提案させる <Tooltip content="1ポイントを消費します"><span className={styles.creditBadge}>1</span></Tooltip></div>
              <div className={styles.optionDescription}>過去のシーンを基にAIが続きのシーンを生成します</div>
            </div>
          </button>

          {/* AI生成の静止画カット (商品/タイトル/エンド)。ストーリーボードモードでは
              フレーム自動生成がスキップされ未完成カードになるため非表示にする。 */}
          {!isStoryboard && (
          <>
          {/* 商品カットを作成 */}
          <button
            type="button"
            className={styles.optionCard}
            onClick={onSelectProductCut}
            disabled={isGenerating}
          >
            <div className={styles.optionIcon}>
              <img src="/endcard_product.svg" alt="" width="60" height="34" />
            </div>
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>商品カットを作成 <Tooltip content="1ポイントを消費します"><span className={styles.creditBadge}>1</span></Tooltip></div>
              <div className={styles.optionDescription}>商品名・商品写真を含むラストカットを生成します</div>
            </div>
          </button>

          {/* タイトルカードを作成 (冒頭・ロゴのみ) */}
          {onSelectTitleCard && (
            <div
              className={`${styles.optionCard} ${!hasLogo || isGenerating || hasTitleCard ? styles.optionCardDisabled : ''}`}
              onClick={hasLogo && !isGenerating && !hasTitleCard ? onSelectTitleCard : undefined}
              role="button"
              tabIndex={hasLogo && !isGenerating && !hasTitleCard ? 0 : -1}
            >
              <div className={styles.optionIcon}>
                <img src="/endcard_logo.svg" alt="" width="60" height="32" />
              </div>
              <div className={styles.optionText}>
                <div className={styles.optionTitle}>タイトルカードを作成 <Tooltip content="1ポイントを消費します"><span className={styles.creditBadge}>1</span></Tooltip></div>
                <div className={styles.optionDescription}>
                  {hasTitleCard ? '既に冒頭のタイトルカードがあります。' : '冒頭にロゴを掲載したカットを生成します。'}
                </div>
              </div>
            </div>
          )}

          {/* エンドカードを作成 */}
          <div
            className={`${styles.optionCard} ${!hasLogo || isGenerating ? styles.optionCardDisabled : ''}`}
            onClick={hasLogo && !isGenerating ? onSelectLogoCut : undefined}
            role="button"
            tabIndex={hasLogo && !isGenerating ? 0 : -1}
          >
            <div className={styles.optionIcon}>
              <img src="/endcard_logo.svg" alt="" width="60" height="32" />
            </div>
            <div className={styles.optionText}>
              <div className={styles.optionTitle}>エンドカードを作成 <Tooltip content="1ポイントを消費します"><span className={styles.creditBadge}>1</span></Tooltip></div>
              <div className={styles.optionDescription}>ロゴを大きく掲載したラストカットを生成します。</div>
              {!hasLogo && (
                <div className={styles.noLogoHint}>
                  {hasBrand ? (
                    <>
                      ブランドにロゴが設定されていません。{' '}
                      <button
                        type="button"
                        className={styles.brandLink}
                        onClick={(e) => { e.stopPropagation(); setShowLogoModal(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                      >
                        {brandName || 'ブランド'}にロゴを設定する &gt;
                      </button>
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle', marginRight: '4px' }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>ブランドが関連付けられていません。
                      <br />
                      <button
                        type="button"
                        className={styles.brandLink}
                        onClick={(e) => { e.stopPropagation(); setShowCreateBrand(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                      >
                        ブランドを新規登録 &gt;
                      </button>
                      <br />
                      <button
                        type="button"
                        className={styles.brandLink}
                        onClick={(e) => { e.stopPropagation(); setShowBrandSelector(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                      >
                        既存のブランドと関連付ける &gt;
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ブランド選択UI（インライン展開） */}
          {showBrandSelector && !hasBrand && (
            <div className={styles.brandSelectorPanel}>
              <div className={styles.brandSelectorHeader}>
                <span>ブランドを選択</span>
                <button
                  type="button"
                  className={styles.closeButton}
                  onClick={() => { setShowBrandSelector(false); setSelectedBrandId(''); }}
                  style={{ width: '24px', height: '24px', fontSize: '1rem' }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
              <select
                className={styles.brandSelectorSelect}
                value={selectedBrandId}
                onChange={(e) => setSelectedBrandId(e.target.value)}
              >
                <option value="">ブランドを選択...</option>
                {brands.map(b => (
                  <option key={b.id} value={b.id}>{b.brand_name}</option>
                ))}
              </select>
              {selectedBrandId && (
                <div className={styles.brandSelectorActions}>
                  <button
                    type="button"
                    className={styles.brandLinkBtn}
                    onClick={() => onBrandLinked?.(selectedBrandId, true)}
                  >
                    この商品とブランド「{selectedBrandName}」を関連付ける
                  </button>
                  <button
                    type="button"
                    className={styles.brandOnceBtn}
                    onClick={() => onBrandLinked?.(selectedBrandId, false)}
                  >
                    一度だけこのブランドを使う
                  </button>
                </div>
              )}
            </div>
          )}
          </>
          )}
          </div>{/* /1列目 */}

          {/* 2列目: 既存の素材を挿入 (冒頭/末尾・AI生成なし・課金なし) */}
          {onAttachFromMaterial && (
            <div className={styles.column}>
              <div className={styles.columnTitle}>既存の素材を挿入</div>
              <div
                className={`${styles.optionCard} ${isGenerating || hasTitleCard ? styles.optionCardDisabled : ''}`}
                onClick={!isGenerating && !hasTitleCard ? () => setMaterialPickerPosition('start') : undefined}
                role="button"
                tabIndex={!isGenerating && !hasTitleCard ? 0 : -1}
              >
                <div className={styles.optionIcon}>
                  <img src="/endcard_logo.svg" alt="" width="60" height="32" />
                </div>
                <div className={styles.optionText}>
                  <div className={styles.optionTitle}>冒頭にタイトルカードを追加</div>
                  <div className={styles.optionDescription}>
                    {hasTitleCard ? '既に冒頭のタイトルカードがあります。' : '保存済みの動画素材を冒頭のタイトルカードとして追加します。'}
                  </div>
                </div>
              </div>
              <div
                className={`${styles.optionCard} ${isGenerating || hasEndcard ? styles.optionCardDisabled : ''}`}
                onClick={!isGenerating && !hasEndcard ? () => setMaterialPickerPosition('end') : undefined}
                role="button"
                tabIndex={!isGenerating && !hasEndcard ? 0 : -1}
              >
                <div className={styles.optionIcon}>
                  <img src="/endcard_logo.svg" alt="" width="60" height="32" />
                </div>
                <div className={styles.optionText}>
                  <div className={styles.optionTitle}>末尾にエンドカードを追加</div>
                  <div className={styles.optionDescription}>
                    {hasEndcard ? '既に末尾のエンドカードがあります。' : '保存済みの動画素材を末尾のエンドカードとして追加します。'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {isGenerating && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingContent}>
              <span className={styles.spinner} />
              <span>{generatingLabel || 'シーンを生成中...'}</span>
              {showLinkPrompt && (
                <div className={styles.linkingPrompt}>
                  <div className={styles.linkingText}>
                    {productBrandInfo?.product_name
                      ? <>「{productBrandInfo.product_name}」をブランド「{brandName}」に関連付けますか？</>
                      : <>この商品をブランド「{brandName}」に関連付けますか？</>
                    }
                  </div>
                  <div className={styles.linkingActions}>
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={onLinkProduct}
                      disabled={linkProductStatus === 'pending'}
                    >
                      {linkProductStatus === 'pending' ? '関連付け中...' : '関連付ける'}
                    </button>
                    <button
                      type="button"
                      className={styles.linkBtnSkip}
                      onClick={onSkipLink}
                    >
                      スキップ
                    </button>
                  </div>
                </div>
              )}
              {linkProductStatus === 'success' && (
                <div className={styles.linkingSuccess}>関連付けました</div>
              )}
            </div>
            <label className={styles.autoGenerateCheckbox}>
              <input
                type="checkbox"
                checked={autoGenerateFrame ?? false}
                onChange={(e) => onAutoGenerateFrameChange?.(e.target.checked)}
              />
              <span>フレームを自動生成</span>
            </label>
          </div>
        )}
      </div>

      <CreateBrandModal
        isOpen={showCreateBrand}
        onClose={() => setShowCreateBrand(false)}
        requireLogoSetup
        scenarioId={scenarioId}
        onBrandCreated={(brandId) => {
          setJustCreatedBrand(true);
          onBrandJustCreated?.(brandId);
        }}
      />

      {showLogoModal && brandId && (
        <LogoManagementModal
          isOpen={showLogoModal}
          onClose={() => {
            setShowLogoModal(false);
            onBrandJustCreated?.(brandId);
          }}
          brandId={brandId}
        />
      )}

      {materialPickerPosition && (
        <MaterialsPickerModal
          aspectRatio={aspectRatio}
          brandId={brandId}
          title={materialPickerPosition === 'start' ? '冒頭タイトルカードの動画素材を選択' : '末尾エンドカードの動画素材を選択'}
          onSelect={(imageUrl, materialId) => {
            const pos = materialPickerPosition;
            setMaterialPickerPosition(null);
            onAttachFromMaterial?.(pos, imageUrl, materialId);
          }}
          onClose={() => setMaterialPickerPosition(null)}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
